import { Flex, PersonCard, Text, Image } from "@capioweb/ui-components/src"
import { PropertyCategory, QBankImage } from "../../@types/CapioWeb/core"
import Property, {
	BooleanProperty,
	LinkProperty,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { BlockCommonLayout } from "../../utils/layout"
import { imageBlockDataToImageProps } from "../../utils"
import React from "react"
import { LocalesContext } from "../../context"
import {
	breakpoints,
	Button,
	HEADER_HEIGHT_S,
	scale,
	Select,
} from "@capioweb/ui-components/src"
import useSSR from "use-ssr"
import { sendContactDataLayerPush } from "../../utils/piwikPro"

interface CoworkerCardProps {
	name: StringProperty
	emailLink: LinkProperty
	phoneLink: LinkProperty
	position: StringProperty
	ingress: StringProperty
	media: QBankImage
	additionalTitle: PropertyCategory
}

type CoworkerContentAreaProperty = Property<CoworkerCardProps[]>

interface CapioNexusCoworkersBlockProps {
	header: StringProperty
	coworkers: CoworkerContentAreaProperty
	filter: FilterItem[]
	enableFilter?: BooleanProperty
	filterLabel?: StringProperty
}

interface FilterItem {
	key: string
	value: string
}

const CapioNexusCoworkersBlock: React.FC<CapioNexusCoworkersBlockProps> = ({
	header,
	coworkers,
	filter,
	enableFilter,
	...rest
}) => {
	const { isServer } = useSSR()

	const { translate, translationKeys } = React.useContext(LocalesContext)

	const [showAll, setShowAll] = React.useState<boolean>(false)

	const [initialItemLimit, setInitialItemLimit] = React.useState<number>(0)

	const [filteredItems, setFilteredItems] = React.useState<CoworkerCardProps[]>(
		coworkers?.value
	)

	const ref = React.useRef(null)

	React.useEffect(() => {
		if (isServer) return

		const width = window.innerWidth

		if (width >= breakpoints.xl) {
			setInitialItemLimit(4)
		} else if (width >= breakpoints.m) {
			setInitialItemLimit(3)
		} else {
			setInitialItemLimit(1)
		}
	}, [])

	const hasItems = filteredItems?.length > 0

	const scrollToRef = () => {
		window.scrollTo(0, ref.current.offsetTop)
		setTimeout(() => {
			const headerOffset = scale(HEADER_HEIGHT_S)
			const elementPosition = ref.current.getBoundingClientRect().top
			const offsetPosition = elementPosition + window.pageYOffset - headerOffset

			window.scrollTo({
				top: offsetPosition,
				behavior: "smooth",
			})
		}, 100)
	}

	const handleFilterChange = (key: string) => {
		if (!key || key === "0") {
			// If key is empty or 0, reset the filtered items to the original list
			setFilteredItems(coworkers?.value)
			return
		}

		// Ensure coworkers and its value property exist before attempting to filter
		if (!coworkers?.value) {
			console.error("Coworkers or coworkers value is undefined")
			return
		}

		// Filter the coworkers based on the selected key
		const updatedFilter = coworkers.value.filter((item) =>
			item.additionalTitle.value?.find((i) => i.id.toString() === key)
		)

		// Ensure updatedFilter is not undefined before setting it
		if (!updatedFilter) {
			console.error("Updated filter is undefined")
			return
		}

		setFilteredItems(updatedFilter)
	}

	return (
		<Flex ref={ref}>
			<BlockCommonLayout heading={header} {...rest}>
				{enableFilter.value && (
					<Flex w={1} justifyContent="end">
						<Flex w={{ _: 1, m: 40 }}>
							<Select
								w={{ _: 1, m: 40 }}
								options={filter.map((item) => ({
									label: item.value,
									value: item.key,
								}))}
								onChange={(event) => {
									handleFilterChange(event.currentTarget.value)
								}}
								label={rest.filterLabel?.value}
							></Select>
						</Flex>
					</Flex>
				)}

				<Flex
					flexWrap={{ _: "nowrap", m: "wrap" }}
					flexDirection={{ _: "column", m: "row" }}
					columnGap={3}
					rowGap={{ _: 2, m: 3 }}
					mb={2.5}
					w={1}
				>
					{hasItems &&
						filteredItems.map((item, i) => (
							<>
								{(showAll || (i < initialItemLimit)) &&

									<PersonCard
										maxw={{
											_: "100%",
											m: "calc(100% / 3 - 16px)",
											xl: "calc(25% - 18px)",
										}}
										key={i}

									>
										<Flex flexDirection="column">
											{item.media?.media?.value?.url && (
												<Image
													{...imageBlockDataToImageProps(item.media, true, true, [
														{ width: 800, breakpoint: 2560 },
													])}
													style={{
														borderRadius: "24px 24px 0 0",
														aspectRatio: "1",
													}}
													cover
												/>
											)}
											<Flex flexDirection="column" pt={3} pb={2} px={3}>
												{item.name.value && (
													<Text variant="headline-3" mb={1}>
														{item.name.value}
													</Text>
												)}
												{item.position?.value && (
													<Text variant="headline-4" mb={1}>
														{item.position.value}
													</Text>
												)}
												{item.name && (
													<Text variant="paragraph-16" mb={1}>
														{item.ingress?.value}
													</Text>
												)}
												{item.emailLink && (
													<Text.Anchor
														href={item.emailLink?.href}
														textDecoration="underline"
														mb={item.phoneLink?.href ? 0 : 1}
														onClick={() =>
															sendContactDataLayerPush("mail", item.emailLink?.href)
														}
													>
														{item.emailLink?.text}
													</Text.Anchor>
												)}
												{item.phoneLink && (
													<Text.Anchor
														href={item.phoneLink?.href}
														textDecoration="underline"
														mb={1}
														onClick={() =>
															sendContactDataLayerPush(
																"phone",
																item.phoneLink?.href
															)
														}
													>
														{item.phoneLink?.text}
													</Text.Anchor>
												)}
											</Flex>
										</Flex>
									</PersonCard>

								}
							</>
						))}
				</Flex>

				{filteredItems.length > initialItemLimit && (
					<Flex>
						<Button
							mt={{ _: 2, m: 3 }}
							variant="ghost"
							icon={showAll ? "minus" : "plus"}
							textColor="navy"
							onClick={() => {
								setShowAll(!showAll)
								showAll && scrollToRef()
							}}
						>
							{showAll
								? translate(translationKeys.viewLess)
								: translate(translationKeys.viewMore)}
						</Button>
					</Flex>
				)}
			</BlockCommonLayout>
		</Flex>
	)
}

export { CapioNexusCoworkersBlock }
